<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-progress-linear
        indeterminate
        :active="isRequestingNow.length > 0"
        color="blue"
        class="mb-0"
      ></v-progress-linear>
      <v-overlay :value="pageLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-flex v-if="!pageLoading && item" style="position: relative">
        <v-container class="grey lighten-5">
          <v-row no-gutters class="pagination-table-header">
            <v-col
              cols="12"
              sm="4"
              class="py-md-0"
            >
              <datetime-picker
                v-model="item.datetime"
                prepend-icon="mdi-calendar-clock"
                :label="lang($store, 'sap/listings.global.filter.hints.datetime')"
                :rules="[rules.required]"
                :locale="getCurrentLocale()"
                :loading="isRequestingNow.includes('datetime')"
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
              class="py-md-0"
            >
              <v-select
                v-model="item.estimation_type"
                :items="estimationTypeItems"
                prepend-icon="mdi-format-list-bulleted-type"
                single-line
                :hint="lang($store, 'sap/listings.global.filter.hints.order_base_type')"
                persistent-hint
                :error-messages="(errors && errors.estimation_type)?errors.estimation_type:null"
                @change="updateRequest('estimation_type', $event); removeError('estimation_type')"
                :loading="isRequestingNow.includes('estimation_type')"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="4"
              class="py-md-0"
            >
              <v-select
                v-model="item.estimation_status"
                :items="estimationStatusItems"
                prepend-icon="mdi-cart-arrow-right"
                single-line
                :hint="lang($store, 'sap/listings.global.filter.hints.order_status')"
                persistent-hint
                :error-messages="(errors && errors.estimation_status)?errors.estimation_status:null"
                @change="updateRequest('estimation_status', $event); removeError('estimation_status')"
                :loading="isRequestingNow.includes('estimation_status')"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="item.name"
                :counter="200"
                :rules="[rules.required, rules.max(200)]"
                :label="lang($store, 'sap/listings.global.filter.hints.name')"
                prepend-icon="mdi-account-badge-outline"
                required
                autocomplete="off"
                :error-messages="(errors && errors.name)?errors.name:null"
                @change="updateRequest('name', $event);"
                @input="removeError('name')"
                :loading="isRequestingNow.includes('name')"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                :value="item.phone"
                :label="lang($store, 'sap/listings.global.filter.hints.phone')"
                v-mask="phoneMask"
                :rules="[rules.required, rules[initPhoneRule]]"
                :error-messages="(errors && errors.phone)?errors.phone:null"
                type="tel"
                prepend-icon="mdi-phone"
                required
                autocomplete="off"
                @change="updateRequest('phone', $event);"
                @input="removeError('phone')"
                :loading="isRequestingNow.includes('phone')"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="item.region"
                :items="regionsItems"
                prepend-icon="mdi-home-city-outline"
                single-line
                :hint="lang($store, 'sap/listings.global.filter.hints.region')"
                persistent-hint
                :error-messages="(errors && errors.region)?errors.region:null"
                @change="updateRequest('region', $event); removeError('region')"
                :loading="isRequestingNow.includes('region')"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="item.note"
                :counter="200"
                :rules="[rules.max(200)]"
                prepend-icon="mdi-tooltip-text-outline"
                :label="lang($store, 'sap/listings.global.filter.hints.note')"
                autocomplete="off"
                :error-messages="(errors && errors.note)?errors.note:null"
                @input="removeError('note')"
                @change="updateRequest('note', $event)"
                :loading="isRequestingNow.includes('note')"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="item.minicomment"
                :counter="200"
                :rules="[rules.max(200)]"
                prepend-icon="mdi-tooltip-text-outline"
                :label="lang($store, 'sap/listings.global.filter.hints.minicomment')"
                autocomplete="off"
                :error-messages="(errors && errors.minicomment)?errors.minicomment:null"
                @input="removeError('minicomment')"
                class="pt-2"
                @change="updateRequest('minicomment', $event)"
                :loading="isRequestingNow.includes('minicomment')"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="item.minicomment2"
                @change="updateRequest('minicomment2', $event)"
                :counter="200"
                :rules="[rules.max(200)]"
                prepend-icon="mdi-tooltip-text-outline"
                :label="lang($store, 'sap/listings.global.filter.hints.minicomment2')"
                autocomplete="off"
                :error-messages="(errors && errors.minicomment2)?errors.minicomment2:null"
                @input="removeError('minicomment2')"
                class="pt-0"
                :loading="isRequestingNow.includes('minicomment2')"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-select
                v-model="item.source"
                :items="maybeWithoutExcluded"
                :background-color="(item.source !== 'unset' && SOURCES_COLORS[item.source])
                ?SOURCES_COLORS[item.source]
                :'white'"
                :item-color="(item.source !== 'unset' && SOURCES_COLORS[item.source])
                ?SOURCES_COLORS[item.source]
                 :'black'"
                class="invert-select"
                :class="item.source"
                prepend-icon="mdi-arrow-decision-outline"
                single-line
                :hint="lang($store, 'sap/listings.global.filter.hints.source')"
                persistent-hint
                :error-messages="(errors && errors.source)?errors.source:null"
                @change="updateRequest('source', $event); removeError('source')"
                :loading="isRequestingNow.includes('source')"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="item.address_formatted"
                :counter="200"
                :rules="[rules.max(200)]"
                prepend-icon="mdi-home-city-outline"
                :label="lang($store, 'sap/listings.global.filter.hints.address')"
                autocomplete="off"
                :error-messages="(errors && errors.address_formatted)?errors.address_formatted:null"
                @input="removeError('address_formatted')"
                @change="updateRequest('address_formatted', $event)"
                :loading="isRequestingNow.includes('address_formatted')"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-textarea
                v-model="item.description"
                :counter="3000"
                :rules="[rules.max(3000)]"
                prepend-icon="mdi-account-badge-horizontal-outline"
                :label="lang($store, 'sap/listings.global.filter.hints.description')"
                autocomplete="off"
                :error-messages="(errors && errors.description)?errors.description:null"
                @input="removeError('description')"
                @change="updateRequest('description', $event)"
                :loading="isRequestingNow.includes('description')"
              ></v-textarea>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-textarea
                v-model="item.result"
                :counter="3000"
                :rules="[rules.max(3000)]"
                prepend-icon="mdi-account-badge-horizontal-outline"
                :label="lang($store, 'sap/listings.global.filter.hints.result')"
                autocomplete="off"
                :error-messages="(errors && errors.result)?errors.result:null"
                @input="removeError('result')"
                @change="updateRequest('result', $event)"
                :loading="isRequestingNow.includes('result')"
              ></v-textarea>
            </v-col>
            <v-col
              v-if="!isForeman"
              cols="12"
              sm="12"
              class="py-md-0"
            >
              <vue-dropzone
                ref="dropzone"
                id="attachmentsUploader"
                :options="dropOptions"
                @vdropzone-complete="afterComplete"
                @vdropzone-success="onFileUploaded"
                @vdropzone-error="onFileError"
              ></vue-dropzone>
            </v-col>
            <v-col
              cols="12"
              sm="12"
              class="py-4"
            >
              <v-row
              v-if="item.attachments.length > 0"
              >
                <v-col
                  cols="12"
                  md="3"
                  sm="4"
                  class="py-md-0"
                  v-for="attachment in item.attachments"
                  :key="attachment.id"
                >
                  <div class="preview-attachment">
                      <a :href="attachment.link" target="_blank" class="preview-attachment__content">
                        <img v-if="isImage(attachment.link)" :src="attachment.link" :alt="attachment.link">
                        <div class="text" v-else>.{{attachment.name.split('.').pop()}}</div>
                      </a>
                    <v-btn
                      x-small fab dark falt fixed top absolute
                      right="right"
                      class="setting-fab"
                      color="red"
                      style="position: absolute; right: 0%; top: 0%;"
                      @click="deleteFile(attachment.id)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-btn
            v-if="!isForeman"
            small fab dark falt fixed
            right="right"
            class="setting-fab"
            color="red"
            style="top: 54%"
            @click="isShowHistory = true"
          >
            <v-icon>mdi-history</v-icon>
          </v-btn>
          <v-navigation-drawer
            v-if="!isForeman"
            class="setting-drawer"
            style="z-index: 99;"
            right v-model="isShowHistory" fixed>
            <keep-alive>
              <activity-list
                v-if="!isForeman"
                :args="[this.item.uuid]"
                method="estimationComplexActivity"
              />
            </keep-alive>
            <div
              class="pa-1"
              @click="isShowHistory = false"
              style="opacity: 0.8; cursor: pointer; background-color: gray;text-align: right; position: absolute; top: 10px; right: 25px; border-radius: 50%; overflow: hidden">
              <v-icon>mdi-close</v-icon>
            </div>
          </v-navigation-drawer>
        </v-container>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import moment from 'moment';
import VueDropzone from 'vue2-dropzone';
/* constants */
import { DATETIME_ISO } from '@/constants/dates';
import { SOURCES_COLORS } from '@/constants/sources_data';
/* utils */
import { prepareValidationMessages } from '@/utils';
/* proxies */
import EstimationComplexProxy from '@/proxies/Estimations/EstimationComplexProxy';
/* mixins */
import accountMixin from '@/mixins/accountMixin';
import updatePageContentMixin from '@/mixins/updatePageContentMixin';
import validationMixin from '@/mixins/validationMixin';
import estimationStatusFilter from '@/mixins/filters/estimationStatusFilter';
import estimationTypeFilter from '@/mixins/filters/estimationTypeFilter';
import regionsFilter from '@/mixins/filters/regionsFilter';
import sourcesFilter from '@/mixins/filters/sourcesFilter';
import modelUpdateSimpleProp from '@/mixins/orders/orderUpdateSimpleProp';
import phoneMixin from "@/mixins/phoneMixin";
/* components */
import DatetimePicker from '@/components/DatetimePicker.vue';
import ActivityList from '@/components/activity/ActivityList.vue';
import i18n from '@/i18n';

export default {
  name: 'ShowComplex',
  mixins: [
    accountMixin,
    updatePageContentMixin,
    validationMixin,
    estimationStatusFilter,
    estimationTypeFilter,
    regionsFilter,
    sourcesFilter,
    modelUpdateSimpleProp,
    phoneMixin,
  ],
  components: {
    DatetimePicker,
    ActivityList,
    VueDropzone,
  },
  data() {
    return {
      SOURCES_COLORS,
      pageLoading: false,
      isShowHistory: false,
      shouldSeeLoaderOnUpdating: true,
      isRequestingNow: [],
      item: null,
      useNullableEstimationStatus: false,
      useNullableEstimationType: false,
      useNullableRegion: false,
      useNullableSource: false,
      errors: {},
      dropOptions: {
        url: `${process.env.VUE_APP_API_LOCATION}/estimations-complex/${this.$route.params.estimationUuid}/attachments`,
        method: 'post',
        maxFilesize: 30, // MB
        maxFiles: 20,
        clickable: true,
        // acceptedFiles: '.png,.jpg,.jpeg,.mov',
        // chunking: true,
        // chunkSize: 500, // Bytes
        // thumbnailWidth: 150, // px
        // thumbnailHeight: 150,
        addRemoveLinks: true,
        headers: {
          'Cache-Control': null,
          'X-Requested-With': null,
          Authorization: `Bearer ${localStorage.getItem('id_token')}`,
          Accept: 'application/json',
        },
        dictDefaultMessage: i18n.t('uploads.dictDefaultMessage'),
        dictFallbackMessage: i18n.t('uploads.dictFallbackMessage'),
        dictFallbackText: i18n.t('uploads.dictFallbackText'),
        dictFileTooBig: i18n.t('uploads.dictFileTooBig'),
        dictInvalidFileType: i18n.t('uploads.dictInvalidFileType'),
        dictResponseError: i18n.t('uploads.dictResponseError'),
        dictCancelUpload: i18n.t('uploads.dictCancelUpload'),
        dictCancelUploadConfirmation: i18n.t('uploads.dictCancelUploadConfirmation'),
        dictRemoveFile: i18n.t('uploads.dictRemoveFile'),
        dictMaxFilesExceeded: i18n.t('uploads.dictMaxFilesExceeded'),
      },
    };
  },
  created() {
    this.pageLoading = true;
    this.fetchData();
  },
  watch: {
    '$route.params.estimationUuid': {
      handler() {
        this.fetchData();
      },
    },
    'item.datetime': {
      handler(newVal) {
        this.updateRequest('datetime', moment(newVal)
          .format(DATETIME_ISO));
      },
    },
  },
  methods: {
    fetchData() {
      new EstimationComplexProxy().find(this.$route.params.estimationUuid)
        .then(response => {
          this.item = response.data.data.estimation;
          this.$route.meta.title = `Оценка: ${this.item.datetime}`;
        })
        .catch(error => {
          console.error(error);
          return this.$router.push({
            name: 404,
          });
        })
        .finally(() => {
          this.pageLoading = false;
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
        });
    },
    getUpdateSimpleRequestClient() {
      return new EstimationComplexProxy();
    },
    updateRequest(property, val) {
      if (!this.isForeman && !this.pageLoading) {
        this.isRequestingNow.push(property);
        (this.updateSimpleRequest(this.item, property))(val)
          .catch(response => {
            this.errors = prepareValidationMessages(response.data.data);
          })
          .finally(() => {
            const index = this.isRequestingNow.indexOf(property);
            if (index !== -1) this.isRequestingNow.splice(index, 1);
          });
      }
    },
    afterComplete(file) {
      this.$refs.dropzone.removeFile(file);
    },
    onFileError(file, response) {
      const errors = prepareValidationMessages(response.data);
      if (errors.file) {
        this.$store.dispatch('page/displaySnackbar', {
          text: errors.file,
          color: 'red darken-3',
          timeout: 6000,
        });
      }
    },
    onFileUploaded() {
      this.fetchData();
    },
    isImage(url) {
      return (url.match(/\.(jpeg|jpg|gif|png)$/) != null);
    },
    deleteFile(fileId) {
      new EstimationComplexProxy().deleteAttachment(this.$route.params.estimationUuid, fileId)
        .then(response => {
          this.fetchData();
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error(error);
          return this.$router.push({
            name: 404,
          });
        })
        .finally(() => {
          this.pageLoading = false;
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
        });
    },
    getCurrentLocale() {
      return window.localStorage.language;
    }
  },
};
</script>

<style scoped lang="scss">
.preview-attachment {
  background-color: #66a0f7;
  width: 100%;
  padding-top: 100%; /* 1:1 Aspect Ratio */
  position: relative; /* If you want text inside of it */
  margin-bottom: 20px;
  &__content {
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    text-decoration: none;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .text {
      display: flex;
      width: 100%;
      height: 100%;
      color: white;
      justify-content: center;
      align-items: center;
      font-weight: bold;
      font-size: 1em;
      text-decoration: none;
    }
  }
}
</style>
